import Phaser from 'phaser';
import { DifficultySystem, DifficultyLevel } from '../systems/DifficultySystem';

const ATTRACT_MODE_DELAY = 45_000; // 45 seconds

export class TitleScene extends Phaser.Scene {
  private idleTimer: number = 0;
  private lastInputTime: number = 0;
  private idleCountdownText?: Phaser.GameObjects.Text;
  private difficultySystem: DifficultySystem;
  private difficultyButtons: Map<DifficultyLevel, Phaser.GameObjects.Text> = new Map();
  private selectedDifficultyText?: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'TitleScene' });
    this.difficultySystem = new DifficultySystem();
  }

  create() {
    const { width, height } = this.cameras.main;
    this.idleTimer = 0;
    this.lastInputTime = Date.now();

    // Title
    this.add.text(width / 2, height / 3 - 40, 'HIVE JUMP', {
      fontSize: '64px',
      color: '#ff6b35',
      fontStyle: 'bold'
    }).setOrigin(0.5);

    this.add.text(width / 2, height / 3 + 20, 'SURVIVORS', {
      fontSize: '48px',
      color: '#00d9ff',
      fontStyle: 'bold'
    }).setOrigin(0.5);

    // Difficulty selector
    this.createDifficultySelector(width / 2, height / 2 - 20);

    // Start button
    const startButton = this.add.text(width / 2, height / 2 + 80, 'CLICK TO START', {
      fontSize: '24px',
      color: '#ffffff',
      backgroundColor: '#2a2a4a',
      padding: { x: 20, y: 10 }
    }).setOrigin(0.5);

    startButton.setInteractive({ useHandCursor: true });

    startButton.on('pointerover', () => {
      startButton.setStyle({ backgroundColor: '#3a3a6a' });
    });

    startButton.on('pointerout', () => {
      startButton.setStyle({ backgroundColor: '#2a2a4a' });
    });

    startButton.on('pointerdown', () => {
      this.startGame();
    });

    // Any click on the canvas also starts the game (belt-and-suspenders for tests)
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Don't start if clicking on difficulty buttons
      const clickedDifficulty = this.getClickedDifficulty(pointer);
      if (clickedDifficulty) {
        this.selectDifficulty(clickedDifficulty);
      } else {
        this.resetIdleTimer();
        this.startGame();
      }
    });

    // Keyboard input resets idle timer
    this.input.keyboard?.on('keydown', () => {
      this.resetIdleTimer();
    });

    // Instructions
    this.add.text(width / 2, height - 80, 'WASD to move | Auto-fire enabled', {
      fontSize: '16px',
      color: '#888888'
    }).setOrigin(0.5);

    // Idle countdown text (hidden initially)
    this.idleCountdownText = this.add.text(width / 2, height - 40, '', {
      fontSize: '18px',
      color: '#aaaaaa',
    }).setOrigin(0.5);
    this.idleCountdownText.setVisible(false);

    // Store game instance for testing
    window.gameInstance = this.game;
    
    // Expose difficulty system for testing
    (this.game as unknown as Record<string, unknown>)['difficultySystem'] = this.difficultySystem;
  }

  private createDifficultySelector(x: number, y: number) {
    const difficulties = this.difficultySystem.getAllDifficulties();
    const buttonSpacing = 140;
    const totalWidth = (difficulties.length - 1) * buttonSpacing;
    const startX = x - totalWidth / 2;

    // Label
    this.add.text(x, y - 35, 'SELECT DIFFICULTY', {
      fontSize: '14px',
      color: '#888888',
    }).setOrigin(0.5);

    difficulties.forEach((diff, index) => {
      const buttonX = startX + index * buttonSpacing;
      const isSelected = diff.level === this.difficultySystem.getCurrentDifficulty();
      const isLocked = diff.locked;

      const button = this.add.text(buttonX, y, isLocked ? '🔒' : diff.config.displayName, {
        fontSize: '16px',
        color: isSelected ? '#00d9ff' : isLocked ? '#666666' : '#ffffff',
        backgroundColor: isSelected ? '#2a4a6a' : isLocked ? '#1a1a2a' : '#2a2a4a',
        padding: { x: 12, y: 8 },
      }).setOrigin(0.5);

      button.setInteractive({ useHandCursor: !isLocked });

      if (!isLocked) {
        button.on('pointerover', () => {
          if (diff.level !== this.difficultySystem.getCurrentDifficulty()) {
            button.setStyle({ backgroundColor: '#3a3a6a' });
          }
        });

        button.on('pointerout', () => {
          if (diff.level !== this.difficultySystem.getCurrentDifficulty()) {
            button.setStyle({ backgroundColor: '#2a2a4a' });
          }
        });

        button.on('pointerdown', () => {
          this.selectDifficulty(diff.level);
        });
      }

      this.difficultyButtons.set(diff.level, button);
    });

    // Selected difficulty description
    this.updateSelectedDifficultyText(x, y + 35);
  }

  private getClickedDifficulty(pointer: Phaser.Input.Pointer): DifficultyLevel | null {
    for (const [level, button] of this.difficultyButtons) {
      const bounds = button.getBounds();
      if (bounds.contains(pointer.x, pointer.y)) {
        return level;
      }
    }
    return null;
  }

  private selectDifficulty(level: DifficultyLevel) {
    // Don't select if locked
    if (level === 'bug_planet' && !this.difficultySystem.isBugPlanetUnlocked()) {
      return;
    }

    this.difficultySystem.setDifficulty(level);
    this.updateDifficultyButtons();
    this.updateSelectedDifficultyText(this.cameras.main.width / 2, this.cameras.main.height / 2 + 15);
    this.resetIdleTimer();
  }

  private updateDifficultyButtons() {
    const difficulties = this.difficultySystem.getAllDifficulties();
    
    for (const diff of difficulties) {
      const button = this.difficultyButtons.get(diff.level);
      if (button) {
        const isSelected = diff.level === this.difficultySystem.getCurrentDifficulty();
        const isLocked = diff.locked;
        
        button.setText(isLocked ? '🔒' : diff.config.displayName);
        button.setStyle({
          color: isSelected ? '#00d9ff' : isLocked ? '#666666' : '#ffffff',
          backgroundColor: isSelected ? '#2a4a6a' : isLocked ? '#1a1a2a' : '#2a2a4a',
        });
      }
    }
  }

  private updateSelectedDifficultyText(x: number, y: number) {
    if (this.selectedDifficultyText) {
      this.selectedDifficultyText.destroy();
    }

    const config = this.difficultySystem.getCurrentConfig();
    this.selectedDifficultyText = this.add.text(x, y, config.description, {
      fontSize: '12px',
      color: '#aaaaaa',
    }).setOrigin(0.5);
  }

  private startGame() {
    const difficulty = this.difficultySystem.getCurrentDifficulty();
    this.scene.start('GameScene', { difficulty });
  }

  update(_time: number, _delta: number) {
    if (this.idleTimer >= ATTRACT_MODE_DELAY) {
      return; // Already in attract mode
    }

    const now = Date.now();
    const timeSinceInput = now - this.lastInputTime;
    this.idleTimer = timeSinceInput;

    // Update countdown display
    const remaining = Math.ceil((ATTRACT_MODE_DELAY - this.idleTimer) / 1000);
    if (remaining > 0) {
      this.idleCountdownText?.setText(`Attract mode in ${remaining}s`);
      this.idleCountdownText?.setVisible(true);
    }

    // Start attract mode after 45 seconds of idle
    if (this.idleTimer >= ATTRACT_MODE_DELAY) {
      this.startAttractMode();
    }
  }

  private resetIdleTimer() {
    this.lastInputTime = Date.now();
    this.idleTimer = 0;
    this.idleCountdownText?.setVisible(false);
  }

  private startAttractMode() {
    // Auto-mute audio when attract mode starts
    // Use the global isGameMuted property to track mute state
    const wasMuted = window.isGameMuted;
    window.isGameMuted = true;

    // Launch attract game scene on top of title (title stays behind)
    // Attract mode always runs at Veteran (Intermediate) difficulty
    this.scene.launch('AttractGameScene', { difficulty: 'veteran' });
    
    // Listen for attract mode end
    this.events.once('attractModeEnd', () => {
      // Restore audio state to what it was before attract mode
      window.isGameMuted = wasMuted;
      
      // Return to title
      this.scene.stop('AttractGameScene');
      this.resetIdleTimer();
    });
  }

  // Public API for testing
  public getTitleState() {
    return {
      currentDifficulty: this.difficultySystem.getCurrentDifficulty(),
      isBugPlanetUnlocked: this.difficultySystem.isBugPlanetUnlocked(),
      difficulties: this.difficultySystem.getAllDifficulties(),
    };
  }
}
